import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { PrestationService } from '../../../../core/services/prestation.service';
import {
  PrestationHistoryParams,
  PrestationHistoryResponse,
  Prestation,
} from '../../../../core/interfaces/prestation.interface';
import { AppState } from '../../../../store/app.state';
import {
  selectAllSubAccounts,
  selectSubAccountsLoading,
} from '../../../../store/sub-accounts/sub-accounts.selectors';
import { selectCurrentUser } from '../../../../store/auth/auth.selectors';
import { ModalService } from '../../../../core/services/modal.service';
import { PrestationDetailsModalComponent } from '../../../../shared/components/modal/prestation-details-modal';
import {
  LucideAngularModule,
  Calendar,
  Clock,
  User,
  FileText,
  Eye,
  Download,
} from 'lucide-angular';

// Interface pour les bénéficiaires
export interface Beneficiary {
  fullname: string;
  passportNumber: string;
}

@Component({
  selector: 'app-medical-history',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule],
  templateUrl: './medical-history.component.html',
  styleUrls: ['./medical-history.component.scss'],
})
export class MedicalHistoryComponent implements OnInit, OnDestroy {
  @Input() beneficiaries?: Beneficiary[];
  @Input() policeExternalId?: string;

  readonly Calendar = Calendar;
  readonly Clock = Clock;
  readonly User = User;
  readonly FileText = FileText;
  readonly Eye = Eye;
  readonly Download = Download;

  private subscription = new Subscription();
  prestations: Prestation[] = [];
  filteredPrestations: Prestation[] = [];
  loading = false;
  error: string | null = null;
  currentPage = 1;
  totalPages = 1;
  totalItems = 0;
  itemsPerPage = 12;
  currentPassportNumber: string = '';
  selectedPassport: string = '';
  subAccounts: any[] = [];
  currentUser: any = null;
  availableBeneficiaries: Beneficiary[] = [];
  searchTerm: string = '';

  constructor(
    private prestationService: PrestationService,
    private store: Store<AppState>,
    private modalService: ModalService
  ) {
    console.log(
      'MedicalHistoryComponent: Constructeur - PrestationService injecté:',
      this.prestationService
    );
    console.log(
      'MedicalHistoryComponent: Constructeur - ModalService injecté:',
      this.modalService
    );
  }

  ngOnInit(): void {
    console.log('MedicalHistoryComponent: Initialisation...');
    console.log('MedicalHistoryComponent: Composant affiché dans le DOM');

    // Surveiller l'utilisateur courant en priorité
    this.subscription.add(
      this.store.select(selectCurrentUser).subscribe((currentUser) => {
        console.log(
          'MedicalHistoryComponent: Utilisateur courant récupéré du store:',
          currentUser
        );
        this.currentUser = currentUser;

        // S'assurer que l'utilisateur connecté est dans la liste des bénéficiaires
        if (
          currentUser?.passportNumber &&
          currentUser?.firstName &&
          currentUser?.lastName
        ) {
          // Vérifier si l'utilisateur connecté est déjà dans la liste
          const userAlreadyInList = this.availableBeneficiaries.some(
            (beneficiary) =>
              beneficiary.passportNumber === currentUser.passportNumber
          );

          if (!userAlreadyInList) {
            // Ajouter l'utilisateur connecté en premier dans la liste
            this.availableBeneficiaries.unshift({
              fullname: `${currentUser.firstName} ${currentUser.lastName}`,
              passportNumber: currentUser.passportNumber,
            });
            console.log(
              'MedicalHistoryComponent: Utilisateur connecté ajouté à la liste des bénéficiaires:',
              currentUser.passportNumber
            );
          }

          // Toujours sélectionner l'utilisateur connecté, même si un autre est déjà sélectionné
          this.selectedPassport = currentUser.passportNumber;
          console.log(
            'MedicalHistoryComponent: Utilisateur connecté sélectionné par défaut:',
            this.selectedPassport
          );
          // Charger les prestations après la sélection
          this.loadPrestations();
        }
      })
    );

    // Si des bénéficiaires sont passés en input, les utiliser
    if (this.beneficiaries && this.beneficiaries.length > 0) {
      console.log(
        'MedicalHistoryComponent: Utilisation des bénéficiaires passés en input:',
        this.beneficiaries
      );
      this.availableBeneficiaries = this.beneficiaries;
    } else {
      // Sinon, surveiller les sous-comptes dans le store
      console.log(
        'MedicalHistoryComponent: Utilisation des sous-comptes du store'
      );
      this.subscription.add(
        this.store.select(selectAllSubAccounts).subscribe((subAccounts) => {
          console.log(
            'MedicalHistoryComponent: Sous-comptes récupérés du store:',
            subAccounts
          );
          this.subAccounts = subAccounts;
          // Convertir les sous-comptes en format Beneficiary
          this.availableBeneficiaries = subAccounts
            .filter((subAccount) => subAccount.passportNumber) // Filtrer les sous-comptes sans numéro de passeport
            .map((subAccount) => ({
              fullname: `${subAccount.firstName} ${subAccount.lastName}`,
              passportNumber: subAccount.passportNumber!,
            }));
        })
      );
    }

    // Vérifier que le service est disponible
    if (!this.prestationService) {
      console.error(
        'MedicalHistoryComponent: PrestationService non disponible'
      );
      this.error = 'Service non disponible';
      return;
    }

    console.log(
      'MedicalHistoryComponent: Service disponible, méthodes:',
      Object.getOwnPropertyNames(Object.getPrototypeOf(this.prestationService))
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    console.log('MedicalHistoryComponent: Souscriptions nettoyées');
  }

  loadPrestations(page: number = 1): void {
    console.log('MedicalHistoryComponent: Début du chargement des prestations');

    if (!this.prestationService) {
      console.error(
        'MedicalHistoryComponent: PrestationService non disponible dans loadPrestations'
      );
      this.error = 'Service non disponible';
      return;
    }

    this.loading = true;
    this.error = null;
    this.currentPage = page;

    const params: PrestationHistoryParams = {
      page: this.currentPage,
      limit: this.itemsPerPage,
      orderBy: 'updatedAt',
      order: 'desc',
      includeMedicalActs: true,
    };

    // Ajouter le filtre de passeport seulement s'il est disponible
    if (this.selectedPassport) {
      params.passportNumber = this.selectedPassport;
    }

    // Ajouter l'ID de police externe si disponible
    if (this.policeExternalId) {
      params.policeExternalId = this.policeExternalId;
    }

    console.log(
      'MedicalHistoryComponent: Chargement des prestations avec params:',
      params
    );

    try {
      this.subscription.add(
        this.prestationService.getPrestationHistory(params).subscribe({
          next: (response: PrestationHistoryResponse) => {
            console.log(
              'MedicalHistoryComponent: Prestations récupérées:',
              response
            );
            this.prestations = response.data || [];

            // Stocker les métadonnées de l'API avant d'appliquer le filtre
            const apiTotalItems =
              response.meta?.totalItems || this.prestations.length;
            const apiTotalPages =
              response.meta?.totalPages ||
              Math.ceil(apiTotalItems / this.itemsPerPage);

            // Appliquer le filtre de recherche
            this.applySearchFilter();

            // Si il n'y a pas de terme de recherche, utiliser les métadonnées de l'API
            if (!this.searchTerm || this.searchTerm.trim() === '') {
              this.totalItems = apiTotalItems;
              this.totalPages = apiTotalPages;
            }
            // Sinon, les valeurs sont déjà mises à jour par applySearchFilter()

            this.loading = false;
          },
          error: (error) => {
            console.error(
              'MedicalHistoryComponent: Erreur lors du chargement des prestations:',
              error
            );
            this.error = 'Erreur lors du chargement des prestations';
            this.loading = false;
          },
        })
      );
    } catch (error) {
      console.error(
        "MedicalHistoryComponent: Erreur lors de l'appel au service:",
        error
      );
      this.error = "Erreur lors de l'appel au service";
      this.loading = false;
    }
  }

  // Méthode pour appliquer le filtre de recherche
  applySearchFilter(): void {
    if (!this.searchTerm || this.searchTerm.trim() === '') {
      this.filteredPrestations = [...this.prestations];
      // Ne pas modifier la pagination si pas de recherche
    } else {
      const searchLower = this.searchTerm.toLowerCase().trim();
      this.filteredPrestations = this.prestations.filter((prestation) => {
        return (
          // Recherche par ID personnalisé
          (prestation.costumId &&
            prestation.costumId.toLowerCase().includes(searchLower)) ||
          // Recherche par type de prestation
          // (prestation.type &&
          //   this.getPrestationType(prestation)
          //     .toLowerCase()
          //     .includes(searchLower)) ||
          // Recherche par catégorie
          // (prestation.category &&
          //   prestation.category.toLowerCase().includes(searchLower)) ||
          // Recherche par nom de structure
          (prestation.structure?.name &&
            prestation.structure.name.toLowerCase().includes(searchLower)) ||
          // Recherche par nom du médecin
          (prestation.doctor?.completeName &&
            prestation.doctor.completeName
              .toLowerCase()
              .includes(searchLower)) ||
          // Recherche par montant (convertir en string pour la recherche)
          (prestation.amount &&
            prestation.amount.toString().includes(searchLower)) ||
          // Recherche par date (format français)
          (prestation.startDate &&
            this.formatDate(prestation.startDate)
              .toLowerCase()
              .includes(searchLower))
        );
      });

      // Mettre à jour la pagination basée sur les résultats filtrés seulement si il y a une recherche
      this.totalItems = this.filteredPrestations.length;
      this.totalPages = Math.ceil(this.totalItems / this.itemsPerPage);

      // Si la page courante dépasse le nombre total de pages après filtrage, revenir à la première page
      if (this.currentPage > this.totalPages && this.totalPages > 0) {
        this.currentPage = 1;
      }
    }
  }

  // Méthode appelée lors de la saisie dans le champ de recherche
  onSearchChange(): void {
    // Retour à la première page seulement si on commence une nouvelle recherche
    if (this.searchTerm && this.searchTerm.trim() !== '') {
      this.currentPage = 1;
    }
    this.applySearchFilter();
  }

  // Méthode pour forcer le rechargement
  reloadPrestations(): void {
    console.log('MedicalHistoryComponent: Rechargement forcé des prestations');
    this.loadPrestations();
  }

  // getPrestationType(prestation: Prestation): string {
  //   const typeMap: Record<string, string> = {
  //     ANALYSIS: 'Analyse',
  //     HOSPITALIZATION: 'Hospitalisation',
  //     CONSULTATION: 'Consultation',
  //     SURGERY: 'Chirurgie',
  //     PHARMACY: 'Pharmacie',
  //     LABORATORY: 'Laboratoire',
  //     RADIOLOGY: 'Radiologie',
  //     PHYSIOTHERAPY: 'Physiothérapie',
  //     AMBULANCE: 'Ambulance',
  //     DENTAL: 'Dentaire',
  //     OPHTHALMOLOGY: 'Ophtalmologie',
  //     PHARMACY_AS_PRESTATION: 'Pharmacie',
  //     AMBULATORY: 'Ambulatoire',
  //     VISIT: 'Visite',
  //     MEDICAL_ACT: 'Acte médical',
  //     MEDICAL_QUOTE: 'Devis',
  //     OTHER: 'Autre',
  //   };
  //   return typeMap[prestation.type] || prestation.type;
  // }

  // getPrestationStatus(prestation: Prestation): {
  //   label: string;
  //   class: string;
  // } {
  //   if (!prestation) {
  //     return { label: 'Inconnu', class: 'bg-gray-100 text-gray-800' };
  //   }

  //   // Basé sur la catégorie et le montant
  //   if (prestation.category === 'MEDICAL') {
  //     return { label: 'Médical', class: 'bg-blue-100 text-blue-800' };
  //   } else if (prestation.category === 'FINANCIAL') {
  //     if (prestation.careAmount > 0) {
  //       return {
  //         label: 'Prise en charge',
  //         class: 'bg-green-100 text-green-800',
  //       };
  //     } else {
  //       return { label: 'À payer', class: 'bg-red-100 text-red-800' };
  //     }
  //   } else {
  //     return { label: 'Administratif', class: 'bg-gray-100 text-gray-800' };
  //   }
  // }

  formatDate(dateString: string): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  formatDateTime(dateString: string): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  formatAmount(amount: number): string {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
    }).format(amount);
  }

  getPaginationArray(): number[] {
    const pages: number[] = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(
      1,
      this.currentPage - Math.floor(maxVisiblePages / 2)
    );
    let endPage = Math.min(this.totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  }

  viewPrestationDetails(prestation: Prestation): void {
    console.log(
      'MedicalHistoryComponent: Affichage des détails de la prestation:',
      prestation
    );

    // Vérifier que le service modal est disponible
    if (!this.modalService) {
      console.error('MedicalHistoryComponent: ModalService non disponible');
      alert('Erreur: Service modal non disponible');
      return;
    }

    console.log(
      'MedicalHistoryComponent: ModalService disponible:',
      this.modalService
    );
    const modalId = this.modalService.open({
      component: PrestationDetailsModalComponent,
      title: `Détails de la prestation ${prestation.costumId || ''}`,
      width: '80vw',
      closable: true,
      data: {
        prestation: prestation,
      },
    });
    this.handlePrestationModalEvents(modalId);
  }

  handlePrestationModalEvents(modalId: string): void {
    console.log('MedicalHistoryComponent: Événement reçu:', modalId);
  }

  viewDocuments(prestation: Prestation): void {
    console.log(
      'MedicalHistoryComponent: Affichage des documents de la prestation:',
      prestation
    );
    // TODO: Implémenter l'affichage des documents
    alert(`Documents de la prestation: ${prestation.costumId}`);
  }

  get Math() {
    return Math;
  }

  selectPassport(passportNumber: string) {
    console.log(
      'MedicalHistoryComponent: Sélection du passeport:',
      passportNumber
    );
    this.selectedPassport = passportNumber;
    this.searchTerm = ''; // Réinitialiser la recherche lors du changement de passeport

    // Recharger les prestations avec le nouveau passeport sélectionné
    if (passportNumber) {
      this.loadPrestations(1); // Retour à la première page
    }
  }

  // Méthode pour forcer la sélection de l'utilisateur connecté
  selectCurrentUserPassport(): void {
    if (this.currentUser?.passportNumber) {
      console.log(
        "MedicalHistoryComponent: Sélection forcée du passeport de l'utilisateur connecté:",
        this.currentUser.passportNumber
      );
      this.selectPassport(this.currentUser.passportNumber);
    }
  }

  // Méthode pour obtenir le nom complet du bénéficiaire sélectionné
  getSelectedBeneficiaryName(): string {
    if (!this.selectedPassport) return '';

    // Si c'est l'utilisateur connecté, utiliser ses informations
    if (this.isCurrentUserSelected()) {
      return `${this.currentUser?.firstName} ${this.currentUser?.lastName}`;
    }

    const beneficiary = this.availableBeneficiaries.find(
      (b) => b.passportNumber === this.selectedPassport
    );

    return beneficiary ? beneficiary.fullname : '';
  }

  // Méthode pour vérifier si l'utilisateur connecté est sélectionné
  isCurrentUserSelected(): boolean {
    return this.currentUser?.passportNumber === this.selectedPassport;
  }
}
